import { HOUR } from "./time.ts";
import { type Reason, reason } from "./reasons.ts";

// Discussion finding (`intel.listening`): which threads are worth an answer.
// Matching and scoring are deterministic; a model may add a verdict on top.
// Ported from the archived server/product/threads.mjs.

// Words that say nothing about the problem on their own.
const STOP = new Set(
  "the and for with your you how what that this are can from into not any all out use using used tool tools best way get have has too very its our more less who why when does doing done want need multiple order view file files free open source app apps new good better easy simple fast large small big work works working instead without other help".split(
    " ",
  ),
);
// "logs" and "log" are one word for matching.
const stem = (w: string) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w);
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const CJK = /[一-鿿]/;

/** Distinct content words of the queries, for matching thread text. */
export function termsOf(queries: string[]): string[] {
  return [
    ...new Set(
      queries.flatMap((q) =>
        String(q)
          .toLowerCase()
          .split(/[^a-z0-9一-鿿]+/)
          .filter((w) => w.length >= 3 && !STOP.has(w))
          .map(stem),
      ),
    ),
  ];
}

/** The terms a text contains, each matched at a word start ("log" in "logs", not "blog"). */
export function termHits(text: string, terms: string[]): string[] {
  const t = String(text || "").toLowerCase();
  return terms.filter((w) => (CJK.test(w) ? t.includes(w) : new RegExp(`(^|[^a-z0-9])${escapeRe(w)}`).test(t)));
}

/** A query matches only through its content words; one made of common words alone ("less") never can. */
export const queryTerms = (query: string) => termsOf([query]);

/**
 * A query cut to its longest content words, for search engines that require
 * every word (Stack Exchange): local matching then decides what fits.
 */
export function shortQuery(query: string, words = 2): string {
  return queryTerms(query)
    .sort((a, b) => b.length - a.length || a.localeCompare(b))
    .slice(0, words)
    .join(" ");
}

const QUERY_SOURCE_RANK: Record<string, number> = { person: 0, model: 1, rules: 2 };

/** How many queries a scan searches. */
export const SCAN_QUERIES = 6;

/**
 * The queries a scan searches with, best first: the person's own, then the
 * model's, then the rules'; within each, the more specific first (more
 * content words), then the older. A query without a content word is never
 * searched: nothing could ever match it, and its results only add noise.
 */
export function pickQueries(items: { value: string; source: string }[], limit: number): string[] {
  return items
    .map((w, i) => ({ value: w.value, rank: QUERY_SOURCE_RANK[w.source] ?? 3, terms: queryTerms(w.value).length, i }))
    .filter((w) => w.terms > 0)
    .sort((a, b) => a.rank - b.rank || b.terms - a.terms || a.i - b.i)
    .slice(0, limit)
    .map((w) => w.value);
}

export interface ThreadCandidate {
  kind: "post" | "comment";
  title: string;
  excerpt: string;
  comments: number | null;
  created_at: string;
}

/**
 * The query a thread matches: at least two words of the same query (one
 * intent), or its only word. Common words from different queries
 * ("search" + "message") are a coincidence.
 */
export function bestQuery(thread: ThreadCandidate, queries: string[]): string | null {
  const text = thread.kind === "comment" ? thread.excerpt : `${thread.title} ${thread.excerpt}`;
  const best = queries
    .map((q) => ({ q, terms: termsOf([q]) }))
    .map((p) => ({ ...p, hits: termHits(text, p.terms).length }))
    .filter((p) => p.terms.length && p.hits >= Math.min(2, p.terms.length))
    .sort((a, b) => b.hits - a.hits)[0];
  return best?.q ?? null;
}

// A question or a request for a tool; "How we built X" is neither.
const QUESTION =
  /\?|^ask hn\b|\b(is there|are there|anyone know|looking for|recommend|alternatives? to|what do you use)\b/i;
const COMMENT_ASK = /\b(any|anyone|is there|are there|looking for|recommend|alternatives?)\b[^.!?]{0,120}\?/i;

export interface ThreadScore {
  relevance: number;
  reasons: Reason[];
  hits: number;
}

/** A deterministic score: fresh, on topic, a question, a live discussion. */
export function scoreThread(t: ThreadCandidate, terms: string[], now: number): ThreadScore {
  const reasons: Reason[] = [];
  const age = (now - Date.parse(t.created_at)) / HOUR;
  let s = age <= 24 ? 1 : age <= 72 ? 0.7 : age <= 168 ? 0.35 : 0;
  reasons.push(reason("posted_ago", { hours: Math.max(0, Math.round(age)) }));
  // A comment is judged on its own words; its story title is only context.
  const hits = termHits(t.kind === "comment" ? t.excerpt : `${t.title} ${t.excerpt}`, terms);
  s += Math.min(0.6, hits.length * 0.2);
  if (hits.length) reasons.push(reason("mentions_terms", { terms: hits.slice(0, 3).join(", ") }));
  const asking = t.kind === "comment" ? COMMENT_ASK.test(t.excerpt) : QUESTION.test(t.title);
  if (asking) {
    s += 0.3;
    reasons.push(reason(t.kind === "comment" ? "comment_asks_for_tool" : "author_asks"));
  }
  if (t.kind === "comment") reasons.push(reason("is_comment_on", { title: String(t.title).slice(0, 60) }));
  const comments = t.comments ?? 0;
  if (comments >= 2 && comments <= 150) {
    s += 0.2;
    reasons.push(reason("active_discussion", { comments }));
  } else if (comments > 300) s -= 0.2;
  return { relevance: Math.round(s * 100) / 100, reasons, hits: hits.length };
}

export interface Community {
  value: string;
  scannable: boolean;
}

const SE_DOMAINS: Record<string, string> = {
  "stackoverflow.com": "stackoverflow",
  "superuser.com": "superuser",
  "serverfault.com": "serverfault",
  "askubuntu.com": "askubuntu",
};

/** The Stack Exchange API's name for the site at a host ("unix" for unix.stackexchange.com); null for other hosts. */
export function stackExchangeSite(host: string): string | null {
  const h = host.toLowerCase().replace(/^www\./, "");
  if (SE_DOMAINS[h]) return SE_DOMAINS[h];
  const m = /^([a-z0-9-]+)\.stackexchange\.com$/.exec(h);
  return m && !["meta", "api", "chat", "data"].includes(m[1]) ? m[1] : null;
}

/** The host of a Stack Exchange site from its API name. */
export function stackExchangeHost(site: string): string {
  const own = Object.entries(SE_DOMAINS).find(([, name]) => name === site);
  return own ? own[0] : `${site}.stackexchange.com`;
}

/**
 * "HN", "news.ycombinator.com" → hackernews; "/r/rust", reddit URLs → r/rust;
 * a Stack Exchange site's domain, a link into it or "se/unix" → se/unix.
 */
export function normalizeCommunity(raw: unknown): Community | null {
  const v = String(raw ?? "").trim();
  if (/^(hn|hacker ?news|news\.ycombinator\.com)$/i.test(v)) return { value: "hackernews", scannable: true };
  const m = /^(?:https?:\/\/(?:www\.)?reddit\.com)?\/?r\/([A-Za-z0-9_]{2,21})\/?$/i.exec(v);
  if (m) return { value: `r/${m[1]}`, scannable: true };
  const se = /^se\/([a-z0-9-]{2,40})$/i.exec(v);
  if (se) return { value: `se/${se[1].toLowerCase()}`, scannable: true };
  const bare = /^(stack ?overflow|super ?user|server ?fault|ask ?ubuntu)$/i.exec(v);
  if (bare) return { value: `se/${bare[1].replace(/\s+/g, "").toLowerCase()}`, scannable: true };
  if (/^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i.test(v)) {
    try {
      const site = stackExchangeSite(new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`).hostname);
      if (site) return { value: `se/${site}`, scannable: true };
    } catch {
      // Not a URL after all; kept as a note below.
    }
  }
  if (v.length >= 2 && v.length <= 40) return { value: v, scannable: false };
  return null;
}

/**
 * When a discussion opportunity stops being worth acting on: `hours` after the
 * thread was posted, as its source's definition says (intel.listening in
 * knowledge/). Threads age from when they were posted, not from when they were
 * found. Null when the source has no window: nothing from it is an opportunity.
 */
export function discussionExpiry(postedAt: string, hours: number | undefined): number | null {
  if (hours === undefined || !(hours > 0)) return null;
  return Date.parse(postedAt) + hours * HOUR;
}
